// Shared runtime helper used by every environment.ts variant. Derives the backend
// origin from window.location so the SAME Angular build works whether it's:
//
//   1. Served same-origin with the API — e.g. the LocalServer path-prefix setup
//      (`http://<lan-ip>:5080/<app-prefix>/` hitting `/api/...` on the same host),
//      OR a production deployment that also uses path-prefix.
//   2. Served from a frontend-only subdomain that pairs with an `app.*` backend
//      subdomain — e.g. the classic emeni.rs setup where `konobar.emeni.rs`,
//      `kd.emeni.rs`, `admin.emeni.rs` all pair with `app.emeni.rs`.
//
// Detection rule: if the current hostname begins with a known frontend subdomain
// (konobar, kd, orders, kiosk, admin, ...), we assume cross-origin mode and
// swap that subdomain → `app.`. Otherwise we stay same-origin.
//
// No process.env / no build-time flags — one bundle, runtime-aware.

const FRONTEND_SUBDOMAINS = [
  'konobar',
  'kd',
  'orders',
  'cd',
  'kiosk',
  'admin',
  'sajam',
  'ds',
  'loyalty',
  'user',
  'pos',
];

export interface BackendOrigin {
  /** Full scheme+host, e.g. `"https://app.emeni.rs"` or `"http://localhost:5080"`. */
  origin: string;
  /** Host component only (host[:port]), e.g. `"app.emeni.rs"` or `"localhost:5080"`. */
  host: string;
}

// Runtime-only override (NOT persisted). Set when the WS `auth` response carries a
// `server` value that differs from the current backend (see WSService.handleServerSwitch).
// Reset on every page reload because the module re-evaluates — so the app always starts
// from its natural/main domain and only switches once the WS auth tells it to.
let serverOverrideHost: string | null = null;

/** Pin all backend traffic (HTTP + WS) to `host` until cleared or page reload. */
export function setBackendServerOverride(host: string): void {
  serverOverrideHost = host;
}

/** Drop the override and fall back to the natural origin derived from window.location. */
export function clearBackendServerOverride(): void {
  serverOverrideHost = null;
}

/** True while a runtime server override (from WS `auth.server`) pins traffic away from the natural origin. */
export function hasBackendServerOverride(): boolean {
  return serverOverrideHost !== null;
}

export function deriveBackendOrigin(): BackendOrigin {
  // SSR / test environments — `window` may not exist. Return empty strings; callers
  // that care will fall back to hard-coded defaults.
  if (typeof window === 'undefined' || !window.location) {
    return { origin: '', host: '' };
  }

  // Runtime server-switch override (from WS `auth.server`) takes precedence.
  if (serverOverrideHost) {
    return {
      origin: `${window.location.protocol}//${serverOverrideHost}`,
      host: serverOverrideHost,
    };
  }

  return deriveNaturalBackendOrigin();
}

/** Origin derived purely from window.location, ignoring any runtime override. */
export function deriveNaturalBackendOrigin(): BackendOrigin {
  if (typeof window === 'undefined' || !window.location) {
    return { origin: '', host: '' };
  }

  const { protocol, hostname, port } = window.location;
  const parts = hostname.split('.');

  // Cross-origin mode: <frontend>.emeni.rs → app.emeni.rs
  if (parts.length >= 3 && FRONTEND_SUBDOMAINS.includes(parts[0].toLowerCase())) {
    parts[0] = 'app';
    const backendHost = port ? `${parts.join('.')}:${port}` : parts.join('.');
    return {
      origin: `${protocol}//${backendHost}`,
      host: backendHost,
    };
  }

  // Same-origin mode (LocalServer path-prefix, or same-origin prod).
  return {
    origin: window.location.origin,
    host: window.location.host,
  };
}

/**
 * Origin DRUGE app iz svite, izveden iz trenutnog hosta zamenom frontend poddomena
 * (`admin.dev.emeni.rs` + `'kiosk'` → `https://kiosk.dev.emeni.rs`).
 *
 * Koristi ga org-CSS preview: admin portal uokviruje pravu app, a app u okviru računa
 * OČEKIVANI admin origin da proveri pošiljaoca postMessage-a. Ista detekcija kao
 * `deriveNaturalBackendOrigin` — u same-origin režimu (LocalServer path-prefix, localhost)
 * app-ovi nemaju svoj poddomen, pa vraća `null` i pozivalac tiho odustaje.
 */
export function deriveAppOrigin(subdomain: string): string | null {
  if (typeof window === 'undefined' || !window.location || !subdomain) {
    return null;
  }

  const { protocol, hostname, port } = window.location;
  const parts = hostname.split('.');

  if (parts.length < 3 || !FRONTEND_SUBDOMAINS.includes(parts[0].toLowerCase())) {
    return null;
  }

  parts[0] = subdomain;
  const host = port ? `${parts.join('.')}:${port}` : parts.join('.');
  return `${protocol}//${host}`;
}
